import { useMemo } from 'react';
import { gql, useMutation } from '@apollo/client';
import { MediaDropRegion, presignedS3Uploader, type UploadItem } from '@arthelper/ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const GENERATE_SIGNED_URL = gql`
  mutation GenerateSignedUrl($input: SignedUrlInput!) {
    generateSignedUrl(input: $input)
  }
`;

interface ImageUploadProps {
  onUploadSuccess: (url: string) => void;
  onUploadError: (error: string) => void;
  className?: string;
  existingImageUrl: string;
}

function ImageUpload({ onUploadSuccess, onUploadError, className, existingImageUrl }: ImageUploadProps) {
  const { t } = useTranslation('artwork');
  const [generateSignedUrl] = useMutation(GENERATE_SIGNED_URL);
  const uploader = useMemo(
    () =>
      presignedS3Uploader<string>({
        presign: async (file) => {
          const signed = (
            await generateSignedUrl({
              variables: {
                input: {
                  fileName: file.name,
                  fileType: file.type,
                  type: 'public',
                },
              },
            })
          ).data?.generateSignedUrl;
          if (!signed) throw new Error(t('image-upload.error-signed-url'));
          return { url: signed, contentType: file.type };
        },
        finalize: async (_file, signed) => signed.url.split('?')[0],
      }),
    [generateSignedUrl, t]
  );
  const existing = useMemo(
    () =>
      existingImageUrl
        ? [
            {
              id: `spark:${existingImageUrl}`,
              url: existingImageUrl,
              kind: 'image' as const,
              name: t('image-upload.preview-alt'),
            },
          ]
        : [],
    [existingImageUrl, t]
  );

  return (
    <div className={clsx('w-full', className)}>
      {existingImageUrl && (
        <img
          src={existingImageUrl}
          alt={t('image-upload.preview-alt')}
          className="mb-2 aspect-square w-full rounded object-cover"
        />
      )}
      <MediaDropRegion
        width="100%"
        tone="neutral"
        prompt={t('image-upload.preview-alt')}
        hint="JPG, PNG, GIF, WebP"
        accept={['image/']}
        multiple={false}
        existing={existing}
        uploader={uploader}
        onItemComplete={(item: UploadItem) => onUploadSuccess(item.result as string)}
        onItemError={(_item: UploadItem, error: Error) => onUploadError(error.message)}
      />
    </div>
  );
}

export default ImageUpload;
